import { useEffect, useRef, useState } from 'react';
import EnvironmentalCredentials from '../../../components/fleet/EnvironmentalCredentials';
import PpClimateAlignmentChart from '../../../components/fleet/PpClimateAlignmentChart';
import VesselsDetails from '../../../components/fleet/VesselsDetails';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../../core/store';
import { greenActions, selectFleetCiiAlignment } from '../../../greenSlice';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { IVesselFormData } from '../../../../../shared/sharedTypes';
import { selectEnvironmentalCredentialsMaxYear } from '../../../greenSelector';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';

interface IOverview {
	vessels: IVesselFormData[];
}

const Overview: React.FC<IOverview> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const fleetCiiAlignmentData = useAppSelector(selectFleetCiiAlignment);
	const environmentalCredentilsMaxYear = useAppSelector(selectEnvironmentalCredentialsMaxYear);

	const { msiEnvironmentalCredentialsData } = useAppSelector((state: RootState) => state.greenSlice);

	useEffect(() => {
		if (props.vessels && props.vessels.length > 0) {
			const imoNumbers = props.vessels.map((v) => v.imoNumber);
			dispatch(greenActions.getMsiEnvironmentalCredentialsRequestAction(imoNumbers));
		}
	}, [dispatch, props.vessels]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-col gap-4 w-full">
			<div className="flex basis-1/2 gap-4">
				<div className="flex flex-initial justify-center gap-1 border border-msi-darkgrey-color-300 rounded">
					{(msiEnvironmentalCredentialsData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
						<EnvironmentalCredentials
							portfolioEnvironmentalCredential={
								msiEnvironmentalCredentialsData?.portfolioEnvironmentalCredentials?.portfolioEnvironmentalCredential
							}
							fleetCiiAlignment={fleetCiiAlignmentData.fleetCiiAlignmentData?.ciiAlignment}
							year={environmentalCredentilsMaxYear}
						/>
					)}
				</div>

				<div className="flex flex-1 items-center justify-center gap-1 border border-msi-darkgrey-color-300 rounded">
					{(msiEnvironmentalCredentialsData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
						<ExpandableContainer panelOffsets={panelOffset} title="Portfolio AER Estimate vs Trajectory">
							<PpClimateAlignmentChart
								chartData={msiEnvironmentalCredentialsData?.portfolioEnvironmentalCredentials?.portfolioLocationChartData}
							/>
						</ExpandableContainer>
					)}
				</div>
			</div>

			<div className="flex basis-1/2 items-center justify-center border border-msi-darkgrey-color-300 rounded p-1">
				{(msiEnvironmentalCredentialsData?.loading && <LoadingSpinner size="md" spinnerClassName="p-2" />) || (
					<ExpandableContainer panelOffsets={panelOffset}>
						<VesselsDetails
							data={msiEnvironmentalCredentialsData?.portfolioEnvironmentalCredentials?.environmentalCredential || []}
							year={msiEnvironmentalCredentialsData?.portfolioEnvironmentalCredentials?.aerYear}
						/>
					</ExpandableContainer>
				)}
			</div>
		</div>
	);
};

export default Overview;
